/**
 * useKeyboardSpin — host 鍵盤綁定:
 *   - **空白鍵 spin** — keyboard 在 host 時按 space → 透過 postMessage 送 SPIN 給遊戲
 *   - **focus 搶回** — Cocos iframe 拿走焦點時(window.blur)搶回來,確保 spacebar
 *     事件不會跑到 iframe 內被吞掉
 *
 * # 共同的「不該觸發」條件
 *
 *   - 有 modal 開啟(modalStack.length > 0)→ 把鍵盤 / 焦點留給 modal
 *   - 遊戲已退出(gameExited === true)→ 已切到 exit screen,不該再送 spin / 搶 focus
 *
 * # 用法(App.vue)
 *
 *   useKeyboardSpin({
 *     modalStack: modal.modalStack,
 *     gameExited,
 *     sendSpin: () => postMessage.sendSpinToGame(),
 *   })
 *
 * Lifecycle 自動處理:onMounted 註冊 keydown / blur listener,onBeforeUnmount cleanup。
 */

import { onBeforeUnmount, onMounted, type Ref } from 'vue'

export interface UseKeyboardSpinDeps {
  /** modal 堆疊 — 用來判斷有沒有 modal 在前景(只取 length,不關心 modal 內容) */
  modalStack: Readonly<Ref<readonly unknown[]>>
  /** 遊戲是否已退出 — 退出後不該再 spin / 搶 focus */
  gameExited: Readonly<Ref<boolean>>
  /** 真正送 SPIN 給遊戲(postMessage.sendSpinToGame 包一層) */
  sendSpin: () => void
}

export function useKeyboardSpin(deps: UseKeyboardSpinDeps): void {
  const { modalStack, gameExited, sendSpin } = deps

  function shouldSkip(): boolean {
    return modalStack.value.length > 0 || gameExited.value
  }

  /** 當焦點移到 iframe 時,搶回焦點確保鍵盤事件在 Host 上 */
  function onWindowBlur(): void {
    if (shouldSkip()) return
    requestAnimationFrame(() => {
      window.focus()
    })
  }

  const SPIN_THROTTLE_MS = 500
  let _lastSpinAt = 0

  /** 空白鍵觸發 Spin（throttle 500ms，按住可連發但不會洗爆） */
  function onKeyDown(event: KeyboardEvent): void {
    if (event.code !== 'Space') return
    if (shouldSkip()) return
    event.preventDefault()
    const now = Date.now()
    if (now - _lastSpinAt < SPIN_THROTTLE_MS) return
    _lastSpinAt = now
    sendSpin()
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('blur', onWindowBlur)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('blur', onWindowBlur)
  })
}
